import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { WordType } from '@goldcast/player-config/src/types/transcript-types';
import { getClipBroadcastId } from '@goldcast/player-config/src/utils/clipUtils';
import { selectTranscript } from '@goldcast/player-config/src/selectors/contentMetadataStoreSelectors';
import useSplits, { SplitMode } from '../../hooks/useSplits';
import TooltipV2 from '@/components/TooltipV2';
import Button from '@/components/atoms/Button/Button';
import Icon, { IconName } from '@/components/atoms/Icon';
import DynamicPopup from '@/components/atoms/DynamicPopup/DynamicPopup';
import { classnames } from '@/libs/utils';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useContentMetadataStore } from '@/stores/contentMetaDataStore/contentMetaDataStore';

const SPLIT_DROPDOWN_OPTIONS: {
  mode: SplitMode;
  label: string;
  description: string;
  iconName: IconName;
}[] = [
  { mode: 'precise', label: 'Precise', description: 'Exact playhead position', iconName: 'IconCursorText' },
  { mode: 'word', label: 'Snap to word', description: 'Nearest word boundary', iconName: 'IconLetterCase' },
  {
    mode: 'sentence',
    label: 'Snap to sentence',
    description: 'Nearest sentence boundary',
    iconName: 'IconBlockquote'
  }
];

function SplitButton() {
  const { canUseSplits, reasonForDisabledSplits, performSplit } = useSplits();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { clipData } = useClipsContext();
  const broadcastId = getClipBroadcastId(clipData) ?? '';

  const transcript = useContentMetadataStore(selectTranscript(broadcastId));
  const hasTranscriptWords = transcript.some(w => w.type !== WordType.Punctuation);

  const handlePrimaryClick = useCallback(() => {
    if (!canUseSplits) {
      return;
    }
    performSplit(hasTranscriptWords ? 'word' : 'precise');
  }, [canUseSplits, hasTranscriptWords, performSplit]);

  const handleDropdownTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canUseSplits) {
        return;
      }
      setIsDropdownOpen(open => !open);
    },
    [canUseSplits]
  );

  const handleOptionSelect = useCallback(
    (mode: SplitMode) => {
      performSplit(mode);
      setIsDropdownOpen(false);
    },
    [performSplit]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDropdownOpen]);

  let primaryButtonTooltip: string;
  if (!canUseSplits) {
    primaryButtonTooltip = reasonForDisabledSplits;
  } else if (hasTranscriptWords) {
    primaryButtonTooltip = 'Split at word';
  } else {
    primaryButtonTooltip = 'Split';
  }

  return (
    <div ref={containerRef} className="relative flex">
      <TooltipV2
        tooltipId="split-button-tooltip"
        tooltipContent={primaryButtonTooltip}
        noArrow
        place="top"
        positionStrategy="fixed"
        className="z-10"
        tooltipElement={
          <Button
            className={classnames(
              'relative flex size-[42px] items-center justify-center !p-0',
              hasTranscriptWords ? 'rounded-r-none border-r-0' : ''
            )}
            variation="checkbox"
            trackingId="split-button-primary"
            onClick={handlePrimaryClick}
            buttonSize="base"
            disabled={!canUseSplits}
            onMouseDown={e => e.stopPropagation()}
          >
            <Icon name="IconScissors" size={15} className="timeline-icon" stroke={2.5} />
            <div className="absolute -right-1 -top-1 z-10 flex items-center justify-center rounded-md bg-card-hover-bg px-1 py-0">
              <span className="text-2xs font-medium text-theme-active">Beta</span>
            </div>
          </Button>
        }
      />
      {hasTranscriptWords && (
        <>
          <Button
            className={classnames(
              'flex size-[32px] h-[42px] items-center justify-center rounded-l-none !p-0',
              canUseSplits ? '' : 'pointer-events-none opacity-50'
            )}
            variation="checkbox"
            trackingId="split-button-dropdown-trigger"
            onClick={handleDropdownTriggerClick}
            buttonSize="base"
            disabled={!canUseSplits}
            onMouseDown={e => e.stopPropagation()}
          >
            <Icon
              name={isDropdownOpen ? 'IconChevronUp' : 'IconChevronDown'}
              size={15}
              className="timeline-icon"
              stroke={2.5}
            />
          </Button>
          <DynamicPopup
            triggerRef={containerRef}
            isVisible={isDropdownOpen}
            preferredPosition="bottom"
            offset={4}
            viewportPadding={16}
          >
            <div
              className="min-w-[200px] rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
              role="menu"
              aria-label="Split options"
              data-testid="split-button-dropdown"
            >
              {SPLIT_DROPDOWN_OPTIONS.map(({ mode, label, description, iconName }) => (
                <button
                  key={mode}
                  data-testid={`split-option-${mode}`}
                  type="button"
                  className="flex w-full items-start gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                  onClick={() => handleOptionSelect(mode)}
                  role="menuitem"
                >
                  <Icon name={iconName} size={16} stroke={2} className="timeline-icon mt-0.5 shrink-0" />
                  <span className="flex flex-col">
                    <span className="font-medium">{label}</span>
                    <span className="text-xs text-gray-500">{description}</span>
                  </span>
                </button>
              ))}
            </div>
          </DynamicPopup>
        </>
      )}
    </div>
  );
}

export default memo(SplitButton);
